import { useCallback, useRef, useState } from 'react';

/**
 * Turn a pointer gesture on a timeline clip into move/trim callbacks. Generic:
 * it knows nothing about blocks — the caller supplies per-clip `onMove` /
 * `onEnd` / `onClick`. Mechanics mirror useTimelineSeek: an rAF loop reads the
 * latest pointer, edge auto-scrolls, suppresses text selection, and commits
 * once on release. `toSec(clientX)` returns the scroll-aware timeline seconds
 * under the pointer, so `deltaSec` stays correct even while auto-scrolling.
 */
const useTimelineDrag = ({ toSec, autoScroll, thresholdPx = 3 } = {}) => {
  const [draggingId, setDraggingId] = useState(null);
  const activeRef = useRef(false);

  const beginDrag = useCallback(
    (event, { id, onMove, onEnd, onClick } = {}) => {
      if (event.button || !toSec || activeRef.current) return; // primary only
      // A clip drag must not also start a playhead scrub / text selection.
      event.preventDefault();
      event.stopPropagation();

      activeRef.current = true;
      const startX = event.clientX;
      const grabSec = toSec(startX);
      const prevUserSelect = document.body.style.userSelect;
      document.body.style.userSelect = 'none';

      let latestX = startX;
      let moved = false;
      let rafId = 0;

      const frame = () => {
        rafId = 0;
        // Only preview once the gesture crosses the drag threshold (`moved`).
        // Firing onMove for a sub-threshold nudge would set the caller's draft
        // state, but release then takes the click path (not onEnd), so the draft
        // — and its tooltip — would never be cleared until the next real drag.
        if (!moved) return;
        const velocity = autoScroll?.getVelocity(latestX) ?? 0;
        if (velocity) autoScroll.scrollBy(velocity);
        onMove?.(toSec(latestX) - grabSec);
        if (activeRef.current && velocity) {
          rafId = window.requestAnimationFrame(frame);
        }
      };
      const schedule = () => {
        if (!rafId) rafId = window.requestAnimationFrame(frame);
      };

      const onPointerMove = (moveEvent) => {
        latestX = moveEvent.clientX;
        if (Math.abs(latestX - startX) > thresholdPx && !moved) {
          moved = true;
          setDraggingId(id);
        }
        schedule();
      };

      const onPointerUp = (upEvent) => {
        activeRef.current = false;
        if (rafId) window.cancelAnimationFrame(rafId);
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
        document.body.style.userSelect = prevUserSelect;
        setDraggingId(null);
        if (moved) {
          onMove?.(toSec(upEvent.clientX) - grabSec);
          onEnd?.();
        } else {
          onClick?.();
        }
      };

      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
    },
    [toSec, autoScroll, thresholdPx],
  );

  return { beginDrag, draggingId };
};

export default useTimelineDrag;
